import { Column, Line } from "@ant-design/plots";
import { Card, Col, Empty, Row, Select, Space, Spin, Typography } from "antd";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import { useGraphTheme } from "../../../hooks/useGraphTheme";
import { clamp } from "../../../utils/responsiveHelpers";
import {
  abortLatestRequest,
  ANALYTICS_CHART_HEIGHT,
  ANALYTICS_HIT_RATIO_COLOR,
  ANALYTICS_RESOLUTION_DISPLAY_CONFIG,
  beginLatestRequest,
  EXTERNAL_PROBLEMS_API_BASE,
  REGISTERED_PLATFORM_FILTER_OPTIONS,
  reportUnlessCancelled,
  RESOLUTION_FILTER_OPTIONS,
  toPercentNumber
} from "../helper";

const { Text } = Typography;

const RESOLUTION_ENTRIES = Object.entries(ANALYTICS_RESOLUTION_DISPLAY_CONFIG);
const RESOLUTION_LABEL_DOMAIN = RESOLUTION_ENTRIES.map(([, { label }]) => label);
const RESOLUTION_COLOR_RANGE = RESOLUTION_ENTRIES.map(([, { hex }]) => hex);

/**
 * Flattens one day's five resolution counters into one row per resolution, so the
 * stacked column chart can color each segment consistently by resolution label
 * rather than by G2's default categorical palette.
 *
 * @param {Array<object>} points - `trend.points` from the API, one entry per day.
 * @returns {Array<{date: string, resolutionLabel: string, value: number}>}
 */
const buildResolutionSeries = (points) =>
  points.flatMap((point) => {
    const { date } = point;

    return RESOLUTION_ENTRIES.map(([, { label, countField }]) => ({
      date,
      resolutionLabel: label,
      value: point[countField]
    }));
  });

/**
 * Hit ratio, as a whole percentage per day, for the secondary line chart.
 *
 * @param {Array<object>} points
 * @returns {Array<{date: string, hitRatioPercent: number}>}
 */
const buildHitRatioSeries = (points) =>
  points.map(({ date, hitRatio }) => ({
    date,
    hitRatioPercent: toPercentNumber(hitRatio)
  }));

/**
 * Per-day resolution breakdown and hit ratio for the requested window, with an
 * optional platform filter and an optional resolution filter. Days with no
 * activity still carry a zero-valued row for every resolution, so the chart
 * draws the gap rather than skipping it.
 *
 * @param {object} props
 * @param {string} props.ROOT_URL
 * @param {string} props.startDateIso
 * @param {string} props.endDateIso
 */
const TrendChart = ({ ROOT_URL, startDateIso, endDateIso }) => {
  const [platformFilter, setPlatformFilter] = useState(undefined);
  const [resolutionFilter, setResolutionFilter] = useState(undefined);
  const [points, setPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const abortControllerRef = useRef(null);
  const graphTheme = useGraphTheme();

  const resolutionSeries = buildResolutionSeries(points);
  const hitRatioSeries = buildHitRatioSeries(points);

  const resolutionChartConfig = {
    data: resolutionSeries,
    xField: "date",
    yField: "value",
    colorField: "resolutionLabel",
    stack: true,
    scale: {
      color: {
        domain: RESOLUTION_LABEL_DOMAIN,
        range: RESOLUTION_COLOR_RANGE
      }
    },
    ...graphTheme
  };

  const hitRatioChartConfig = {
    data: hitRatioSeries,
    xField: "date",
    yField: "hitRatioPercent",
    color: ANALYTICS_HIT_RATIO_COLOR,
    ...graphTheme
  };

  /**
   * Loads the per-day resolution counts for the chosen window, platform and
   * resolution. Only the latest call writes state, and a failure leaves the
   * previous chart on screen.
   */
  const fetchTrend = useCallback(() => {
    const { signal } = beginLatestRequest(abortControllerRef);

    setIsLoading(true);
    axios
      .get(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/analytics/trend`, {
        signal,
        params: {
          startDate: startDateIso,
          endDate: endDateIso,
          platform: platformFilter,
          resolution: resolutionFilter
        }
      })
      .then((response) => {
        setPoints(response.data.points);
        setHasLoaded(true);
      })
      .catch(reportUnlessCancelled)
      .finally(() => {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      });
  }, [ROOT_URL, startDateIso, endDateIso, platformFilter, resolutionFilter]);

  useEffect(() => {
    fetchTrend();
  }, [fetchTrend]);

  useEffect(() => () => abortLatestRequest(abortControllerRef), []);

  return (
    <Card
      title="Trend"
      extra={
        <Space wrap size={8}>
          <Select
            placeholder="All platforms"
            allowClear
            style={{ width: clamp(140, 200), maxWidth: "100%" }}
            options={REGISTERED_PLATFORM_FILTER_OPTIONS}
            onChange={setPlatformFilter}
          />
          <Select
            placeholder="All resolutions"
            allowClear
            style={{ width: clamp(140, 200), maxWidth: "100%" }}
            options={RESOLUTION_FILTER_OPTIONS}
            onChange={setResolutionFilter}
          />
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      <Spin spinning={isLoading}>
        {!hasLoaded && isLoading && <div style={{ minHeight: ANALYTICS_CHART_HEIGHT }} />}
        {!hasLoaded && !isLoading && <Empty description="The trend could not be loaded." />}
        {hasLoaded && points.length === 0 && <Empty description="No telemetry in this window" />}
        {hasLoaded && points.length > 0 && (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={14}>
              <Text type="secondary">Resolutions per day</Text>
              <Column {...resolutionChartConfig} height={ANALYTICS_CHART_HEIGHT} />
            </Col>
            <Col xs={24} lg={10}>
              <Text type="secondary">Hit ratio per day</Text>
              <Line {...hitRatioChartConfig} height={ANALYTICS_CHART_HEIGHT} />
            </Col>
          </Row>
        )}
      </Spin>
    </Card>
  );
};

export default TrendChart;
